/**
 * Shared date-range presets for the admin top bar. Ranges are produced in the API's
 * date-only `YYYYMMDD` convention so they pass straight through to the backend filter
 * parser. Pure (no React) so both the filter context and the preset buttons can use it.
 */

const MS_PER_DAY = 86_400_000

/**
 * The day the first analytics record was created. The "All time" preset starts here
 * so the window covers every record without relying on a magic "huge day count".
 */
export const ADMIN_EPOCH_DAY = '20240619'

export interface AdminPreset {
	label: string
	/** Stable identity used to highlight the active preset. */
	key: string
	/** The `{ from, to }` (YYYYMMDD) window this preset selects. */
	range: () => { from: string; to: string }
}

/** The quick-pick ranges shown on the right of the admin nav, in display order. */
export const ADMIN_PRESETS: AdminPreset[] = [
	{ label: 'Today', key: 'today', range: () => presetRange(1) },
	{ label: 'Last 7 days', key: '7d', range: () => presetRange(7) },
	{ label: 'Last 30 days', key: '30d', range: () => presetRange(30) },
	{ label: 'All time', key: 'all', range: () => ({ from: ADMIN_EPOCH_DAY, to: todayYYYYMMDD() }) },
]

/** Default window width (calendar days, inclusive) — matches the "Last 30 days" preset. */
export const ADMIN_DEFAULT_WINDOW_DAYS = 30

const toYYYYMMDD = (date: Date): string => {
	const y = date.getUTCFullYear()
	const m = String(date.getUTCMonth() + 1).padStart(2, '0')
	const d = String(date.getUTCDate()).padStart(2, '0')
	return `${y}${m}${d}`
}

/** Today (UTC) as `YYYYMMDD`. */
const todayYYYYMMDD = (): string => {
	const now = new Date()
	return toYYYYMMDD(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())))
}

/** The `{ from, to }` (YYYYMMDD) for an N-day window ending today (UTC), inclusive. */
export const presetRange = (days: number): { from: string; to: string } => {
	const now = new Date()
	const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
	const from = new Date(to.getTime() - (days - 1) * MS_PER_DAY)
	return { from: toYYYYMMDD(from), to: toYYYYMMDD(to) }
}

/** Which preset the current range matches, or null when it's a custom span. */
export const activePreset = (from: string, to: string): AdminPreset | null => {
	for (const preset of ADMIN_PRESETS) {
		const range = preset.range()
		if (range.from === from && range.to === to) return preset
	}
	return null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const prettyDay = (yyyymmdd: string): string => {
	if (yyyymmdd.length !== 8) return yyyymmdd
	const month = Number(yyyymmdd.slice(4, 6)) - 1
	const day = Number(yyyymmdd.slice(6, 8))
	if (month < 0 || month > 11) return yyyymmdd
	return `${MONTHS[month]} ${day}`
}

/**
 * A short human label for the current range — the preset name when one matches
 * ("today" / "last 7 days" / "last 30 days" / "all time"), otherwise the literal
 * "MMM D – MMM D" span. Used in the per-list "N new · {label}" headline.
 */
export const rangeLabel = (from: string, to: string): string => {
	const preset = activePreset(from, to)
	if (preset) return preset.label.toLowerCase()
	return `${prettyDay(from)} – ${prettyDay(to)}`
}
